
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from 'react-helmet-async';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  display_order: number;
  is_visible: boolean;
}

interface FAQProps {
  homepageData?: {
    faqNumber?: string;
    faqHeadline?: string;
  };
}

export const FAQ = ({ homepageData }: FAQProps) => {
  const [openQuestion, setOpenQuestion] = useState<number | null>(null);

  // Fetch FAQs from database
  const { data: faqs = [], isLoading, error } = useQuery({
    queryKey: ['faqs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .eq('is_visible', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data as FAQ[];
    }
  });

  const toggleQuestion = (index: number) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  // Generate FAQ schema markup for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer.replace(/<[^>]*>/g, '') // Strip HTML tags for schema
      }
    }))
  };

  if (isLoading) {
    return (
      <section className="py-20 px-6" aria-label="Frequently Asked Questions">
        <div className="max-w-4xl mx-auto">
          <div className="text-sm text-purple-500 font-medium mb-4">{homepageData?.faqNumber || "004"}</div>
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-16">
            {homepageData?.faqHeadline || "Frequently Asked Questions"}
          </h2>
          <div className="text-center text-gray-500" role="status" aria-live="polite">
            Loading FAQs...
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    console.error('Error loading FAQs:', error);
    return (
      <section className="py-20 px-6" aria-label="Frequently Asked Questions">
        <div className="max-w-4xl mx-auto">
          <div className="text-sm text-purple-500 font-medium mb-4">{homepageData?.faqNumber || "004"}</div>
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-16">
            {homepageData?.faqHeadline || "Frequently Asked Questions"}
          </h2>
          <div className="text-center text-gray-500" role="alert">
            Unable to load FAQs. Please try again later.
          </div>
        </div>
      </section>
    );
  }

  if (faqs.length === 0) {
    return (
      <section className="py-20 px-6" aria-label="Frequently Asked Questions">
        <div className="max-w-4xl mx-auto">
          <div className="text-sm text-purple-500 font-medium mb-4">{homepageData?.faqNumber || "004"}</div>
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-16">
            {homepageData?.faqHeadline || "Frequently Asked Questions"}
          </h2>
          <div className="text-center text-gray-500">No FAQs available at this time.</div>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* SEO Schema Markup */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <section className="py-20 px-6" aria-label="Frequently Asked Questions">
        <div className="max-w-4xl mx-auto">
          <div className="text-sm text-purple-500 font-medium mb-4">{homepageData?.faqNumber || "004"}</div>
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-16">
            {homepageData?.faqHeadline || "Frequently Asked Questions"}
          </h2>
          
          <div className="space-y-1" role="list">
            {faqs.map((faq, index) => (
              <div key={faq.id} className="border-b border-gray-200" role="listitem">
                <button
                  onClick={() => toggleQuestion(index)}
                  className="w-full py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-lg"
                  aria-expanded={openQuestion === index}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <span className="text-lg text-gray-900 pr-8">
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      openQuestion === index ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
                
                {openQuestion === index && (
                  <div 
                    className="pb-6 text-gray-600 animate-in slide-in-from-top-2 duration-200"
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                  >
                    <div 
                      className="prose prose-gray max-w-none prose-a:text-blue-600 prose-a:no-underline hover:prose-a:text-blue-700 prose-a:font-medium prose-a:transition-colors hover:prose-a:underline"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
